// Reading what a VLA approval letter approves, for initial grants and extensions alike.
//
// Every approval letter follows one template. Under "Approved legal assistance" (or "Approved
// further legal assistance" on an extension) each approved item is a heading line followed by
// the fee basis, which names the handbook fee table outright:
//
//   Magistrates' Court - summary hearing
//   Fee: Lump sum - Fee Schedule 1, Table A
//
// So rather than guessing the grant from its wording, the fee table reference decides which
// allowances apply: Table A loads the Table A rows, Table J the breach rows, and so on. The
// letter does not list how many appearances are allowed (it points to the ATLAS pro forma
// invoice); those come from the fee allowance tables.
//
// The grant number carries the extension number after a backslash: 26A875451\0 is the
// original grant, 29A819984\1 the first extension.
//
// These functions take the letter as lines of text in reading order, so they can be tested
// without a PDF. aidLetterClientParser builds those lines from the PDF's text positions.

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

function clean(value = "") {
  return String(value).replace(/\u0000/g, "").replace(/[’‘]/g, "'").replace(/\s+/g, " ").trim();
}

function wordyDateToIso(value = "") {
  const match = clean(value).match(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
  if (!match) return "";
  const month = MONTHS.indexOf(match[2].toLowerCase());
  if (month < 0) return "";
  return `${match[3]}-${String(month + 1).padStart(2, "0")}-${match[1].padStart(2, "0")}`;
}

// The fee table reference as the letter writes it: "Table A", "Table F(i)", "Table K(ii)".
export function normaliseFeeTable(value = "") {
  const match = clean(value).match(/^([A-Z]{1,3}\d?)\s*(?:\(\s*(i{1,3})\s*\))?/i);
  if (!match) return "";
  return `${match[1].toUpperCase()}${match[2] ? `(${match[2].toLowerCase()})` : ""}`;
}

// The grant wording the claim tracker recognises for each fee table. Some tables cover more
// than one court, so the court on the heading line decides.
export function aidTypeForFeeTable(feeTable, { court = "", consolidated = false } = {}) {
  const c = clean(court).toLowerCase();
  const county = c.includes("county");
  const supreme = c.includes("supreme");
  const children = c.includes("children");
  switch (normaliseFeeTable(feeTable)) {
    case "A": return consolidated ? "Summary Consolidated" : "Summary Single";
    case "B": return children ? "Bail - Children's Court" : "Bail";
    case "C": return "Bail - County Court";
    case "D": return "Bail - Supreme Court";
    case "E": return "Indictable committal";
    case "F": return supreme ? "Supreme Court indictable" : "County Court indictable";
    case "F(i)": return "Children's Court indictable";
    case "G": return "County Court sentence appeal";
    case "H": return "County Court conviction appeal";
    case "J": return supreme ? "Supreme Court breach" : "County Court breach";
    case "K": return "Court of Appeal";
    case "K(i)": return "Court of Appeal - DPP appeal";
    case "K(ii)": return "Interlocutory appeal";
    case "L": return "High Court";
    // Table T is only for a client already found unfit whose order is under review. A fitness
    // investigation is funded by a trial grant, so its letter names Table F, not T.
    case "T": return supreme ? "CMIA - Supreme Court" : "CMIA - County Court";
    case "Z": return county ? "Serious offenders - County Court" : "Serious offenders - Supreme Court";
    case "A4": return "Family violence";
    case "A5": return "Personal safety intervention order";
    case "Y": return "Chief examiner";
    case "BBB": return "Appeal costs certificate";
    default: return "";
  }
}

const FEE_LINE = /^Fee\s*:\s*(.+?)\s*-\s*Fee Schedule\s*(\d+[A-Z]?)\s*,\s*Table\s*([A-Z]{1,3}\d?\s*(?:\(\s*i{1,3}\s*\))?)\s*(?:,\s*(.+))?$/i;
const GRANT_NUMBER = /VLA grant number:\s*(\d{2}A\d{6})\s*(?:\\\s*(\d+))?/i;

export function readApprovalLetter(rawLines = []) {
  const lines = rawLines.map(clean).filter(Boolean);
  const result = {
    aidNumber: "",
    extensionNumber: "",
    isExtension: false,
    clientFullName: "",
    lawyerReference: "",
    effectiveDate: "",
    approvedGrants: [],
    exclusions: [],
    conditions: [],
    reconsiderationBy: "",
    contribution: "",
  };

  // Page 1 carries the grant number the letter is about; later page headers are ignored.
  for (let i = 0; i < lines.length; i += 1) {
    const match = lines[i].match(GRANT_NUMBER);
    if (!match) continue;
    result.aidNumber = match[1].toUpperCase();
    // The extension number follows a backslash, on the same line or on its own nearby.
    const loose = match[2] ?? lines.slice(i + 1, i + 4).map((line) => line.match(/^\\\s*(\d+)$/)?.[1]).find(Boolean);
    if (loose !== undefined) result.extensionNumber = String(Number(loose));
    break;
  }

  const text = lines.join("\n");
  result.isExtension = /further legal assistance is approved|approved further legal assistance/i.test(text)
    || Number(result.extensionNumber) > 0;
  if (result.isExtension && !result.extensionNumber) result.extensionNumber = "";

  result.clientFullName = clean(text.match(/Legal aid application\s*-\s*([^\n]+)/i)?.[1] || "");
  result.lawyerReference = clean(text.match(/Lawyer reference:\s*([^\n]+)/i)?.[1] || "");
  result.effectiveDate = wordyDateToIso(text.match(/Assistance is effective from\s+(\d{1,2}\s+[A-Za-z]+\s+\d{4})/i)?.[1] || "");
  result.reconsiderationBy = wordyDateToIso(text.match(/must contact us by\s+(\d{1,2}\s+[A-Za-z]+\s+\d{4})/i)?.[1] || "");

  const obligations = lines.findIndex((line) => /^Your client'?s obligations$/i.test(line));
  if (obligations >= 0 && lines[obligations + 1]) result.contribution = lines[obligations + 1];

  // Each "Fee:" line belongs to the heading line above it.
  const start = lines.findIndex((line) => /^Approved (?:further )?legal assistance$/i.test(line));
  if (start >= 0) {
    for (let i = start + 1; i < lines.length; i += 1) {
      const fee = lines[i].match(FEE_LINE);
      if (!fee) continue;
      const heading = lines[i - 1] || "";
      const [court = "", ...rest] = heading.split(/\s+-\s+/);
      const feeTable = normaliseFeeTable(fee[3]);
      const qualifier = clean(fee[4] || "");
      const consolidated = /consolidat/i.test(`${qualifier} ${heading}`);
      result.approvedGrants.push({
        court: clean(court),
        grant: clean(rest.join(" - ")),
        feeBasis: clean(fee[1]),
        feeSchedule: fee[2],
        feeTable,
        qualifier,
        aidType: aidTypeForFeeTable(feeTable, { court, consolidated }),
      });
    }
  }

  // "this grant will not cover:" followed by a numbered list.
  const notCovered = lines.findIndex((line) => /this grant will not cover:?$/i.test(line));
  if (notCovered >= 0) {
    for (let i = notCovered + 1; i < lines.length; i += 1) {
      const item = lines[i].match(/^\d+\.\s*(.+?)(?:\s+or|\s+and)?\.?$/i);
      if (!item) break;
      result.exclusions.push(item[1]);
    }
  }

  if (/subject to you obtaining current proof of means/i.test(text)) {
    result.conditions.push({
      kind: "proof_of_means",
      text: "Obtain current proof of means and keep it on file. Claims must not be submitted, and extensions cannot be auto-processed, until VLA has been told it is held.",
    });
  }

  return result;
}

// What a matter's next listing runs into, given the letter's exclusions.
export function exclusionFor(appearanceType = "", exclusions = []) {
  const type = clean(appearanceType).toLowerCase();
  if (!type) return "";
  return (exclusions || []).find((item) => {
    const text = clean(item).toLowerCase();
    if (type === "mention") return /mention hearing/.test(text);
    if (/adjourn/.test(type)) return /adjournment/.test(text);
    return false;
  }) || "";
}
